import { useMemo, useRef, useState } from 'react';
// @mui
import {
    Box,
    CircularProgress,
    List,
    ListItemButton,
    Menu,
    MenuItem,
} from '@mui/material';
// hooks
import useLocales from '../../../hooks/useLocales';
import { useMutation, useQuery } from "@apollo/client/react";
import { useSnackbar } from 'notistack';
import useAuth from '../../../hooks/useAuth';
// components
import { UPDATE_LANGUAGE } from '../../../graphql/resolvers/mutations';
import { KeyboardArrowDown } from '@mui/icons-material';
import { GET_LANGUAGES, GET_USER } from '../../../graphql/resolvers/queries';
import { Language } from '../../../@types/common.types';
import { toUiLocales } from '../../../utils/languages';

export default function LanguageDropdown() {
    const [isLoading, setisLoading] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const { allLangs, currentLang, changeLanguage, translate } = useLocales();

    const { enqueueSnackbar } = useSnackbar();

    const { isAuthenticated } = useAuth();

    const anchorEl = useRef<HTMLDivElement>(null);

    const [updateLanguageMutation] = useMutation(UPDATE_LANGUAGE, { refetchQueries: [GET_USER] });

    const { data: languageData } = useQuery<{ languages: Language[] }>(GET_LANGUAGES, {
        fetchPolicy: 'cache-first',
    });

    /**
     * Only offer a locale the backend will actually accept.
     *
     * `allLangs` is necessarily hardcoded — each entry needs a bundled
     * translation file and a flag asset, both compile-time. But `updateLanguage`
     * rejects any locale not flagged `isPlatformLocale`, so a hardcoded entry
     * without a matching backend row produces an error toast rather than a
     * language change. That is exactly how `pt` and `ht` sat here broken.
     * Intersecting the two makes the drift impossible instead of latent.
     *
     * Falls back to `allLangs` while the query is in flight or if it fails —
     * an empty switcher would be a worse failure than a stale one.
     */
    const options = useMemo(() => {
        const platformLocales = toUiLocales(languageData?.languages);
        if (!platformLocales.length) return allLangs;

        return allLangs.filter((lang) =>
            platformLocales.some((locale) => locale.locale === lang.value)
        );
    }, [languageData, allLangs]);

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleChangeLang = async (locale: string) => {
        try {
            setisLoading(true);

            handleClose();

            if (isAuthenticated) {
                await updateLanguageMutation({ variables: { locale: locale } });
            } else {
                sessionStorage.setItem('languageChangeIntent', 'true');
            }

            await changeLanguage(locale);
        } catch (error) {
            enqueueSnackbar(translate('layouts.header.language.errorTryAgain'), {
                variant: 'error',
                autoHideDuration: 5000,
            });
        } finally {
            setisLoading(false);
        }
    };

    return (
        <>
            <List sx={{ p: 0 }}>
                <ListItemButton
                    ref={anchorEl}
                    sx={{
                        gap: 1,
                        py: 1,
                        px: 1.25,
                        borderRadius: 1.5,
                        justifyContent: 'space-between',
                    }}
                    onClick={handleOpen}
                    disabled={isLoading}
                >
                    <Box
                        component="img"
                        src={currentLang.icon}
                        alt={currentLang.label}
                        sx={{
                            width: 24,
                            height: 16,
                            borderRadius: '2px',
                            objectFit: 'cover',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    />
                    {isLoading ? (
                        <CircularProgress size={18} />
                    ) : (
                        <KeyboardArrowDown sx={{ height: 20, width: 20, color: 'text.primary' }} />
                    )}
                </ListItemButton>
            </List>

            <Menu
                open={isOpen}
                onClose={handleClose}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: anchorEl.current?.offsetWidth,
                        },
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        selected={option.value === currentLang.value}
                        onClick={() => handleChangeLang(option.value)}
                        sx={{
                            py: 1,
                            px: 1.5,
                            fontSize: '14px',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Box
                            component="img"
                            src={option.icon}
                            alt={option.label}
                            sx={{
                                width: 24,
                                height: 16,
                                borderRadius: '2px',
                                objectFit: 'cover',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        />
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
